import { cartPricesAsOf } from '@share-a-cart/shared-utils';
import { useAtom } from 'jotai';
import { LottieRefCurrentProps } from 'lottie-react';
import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AmazonManualLoadModal,
  BlockedRetailerModal,
  CartBaseNotes,
  CartItemsList,
  ChunkCartModal,
  ExtensionInstalledModal,
  ExtensionMissingModal,
  GetSubscriptionOffers,
  LoadingCart,
  RequestApprovalModal,
} from 'src/components';
import { cartHasPrimeDeal } from 'src/components/deals/amazonCartDeals';
import {
  useActiveBrowser,
  useCartRowDeals,
  useContinueWithItems,
  useCurrentUser,
  useGetCartQuery,
} from 'src/hooks';
import { getCartIdAtom } from 'src/store';
import type { CartGetData } from 'src/types';
import {
  AMAZON_CHUNK_VARIANT,
  isAmazonLocalCart,
  willAmazonCartChunk,
} from 'src/utils/get/sendCart';
import { GetReviewChunkBanner } from './GetReviewChunkBanner';
import {
  GetReviewFooter,
  GetReviewHeader,
  GetReviewMissingItems,
  GetReviewOwnership,
} from './getReviewBlocks';

type Props = {
  page?: 'get' | 'embed';
};

export const GetReview = ({ page = 'get' }: Props) => {
  const { user } = useCurrentUser();
  const [cartId] = useAtom(getCartIdAtom);
  const [searchParams] = useSearchParams();
  const chunkVariant = AMAZON_CHUNK_VARIANT;
  const [showRequestApprovalModal, setShowRequestApprovalModal] =
    useState(false);

  const lottieRefHeader = useRef<LottieRefCurrentProps | null>(null);
  const lottieRefFooter = useRef<LottieRefCurrentProps | null>(null);

  const sendItems = useContinueWithItems();
  const activeBrowser = useActiveBrowser();

  const cartQuery = useGetCartQuery({ cartId });
  const dealForItem = useCartRowDeals({ cartId, cartQuery, page });
  const hasPrimeDeal = cartHasPrimeDeal(cartQuery.data?.items, dealForItem);

  const cartApprovalStatus = cartQuery.data?.approvalDetails?.at(-1)?.status;

  const cartIsApproved = useCallback(
    () => cartApprovalStatus === 'approved',
    [cartApprovalStatus],
  );

  /**
   * ORGS:
   *   - if the user is of 'member' role
   *     - show the approval modal
   *       - user can skip approval and submit cart, calling this function with skipApproval = true
   *       - user can submit the cart for approval
   *     - if the cart is approved, we don't show the modal
   * EVERYONE ELSE:
   *   - submit the cart
   *     - sendItems checks if the cart is a split cart (items found cheaper elsewhere)
   *
   * @param skipApproval - if the user has skipped approval, we don't need to show the modal
   */
  const handleContinue = async (skipApproval: boolean) => {
    if (
      user?.subscription.tier === 'organization' &&
      user?.subscription.role === 'member' &&
      !skipApproval &&
      !cartIsApproved()
    ) {
      setShowRequestApprovalModal(true);
      return;
    }

    lottieRefHeader.current?.playSegments([0, 30], true);
    lottieRefFooter.current?.playSegments([0, 30], true);

    // Safari does not execute redirects in intervals or timeouts
    if (activeBrowser === 'Safari') {
      sendItems({
        id: cartId,
        cart: cartQuery.data as CartGetData,
        handleSplitNewVendor: false,
        handleSplitOriginalVendor: false,
        page,
      });

      return;
    }

    // wait for the lottie animation to run
    await new Promise((resolve) => setTimeout(resolve, 1000));

    sendItems({
      id: cartId,
      cart: cartQuery.data as CartGetData,
      handleSplitNewVendor: false,
      handleSplitOriginalVendor: false,
      page,
    });

    // TODO - if the user is in an org and they've loaded a cart, prompt them to tag the cart with 'purchased'
    // if (user?.subscription.tier === 'organization') {
    // }

    // lottieRefHeader.current?.setDirection(-1);
    // lottieRefFooter.current?.setDirection(-1);

    lottieRefHeader.current?.playSegments([30, 0], true);
    lottieRefFooter.current?.playSegments([30, 0], true);

    // await new Promise((resolve) => setTimeout(resolve, 1000));

    // lottieRefHeader.current?.setDirection(1);
    // lottieRefFooter.current?.setDirection(1);
  };

  const handleChunkBannerClick = () => {
    if (!cartQuery.data) return;
    sendItems({
      id: cartId,
      cart: cartQuery.data,
      handleSplitNewVendor: false,
      handleSplitOriginalVendor: false,
      forceChunk: true,
      page,
    });
  };

  return (
    <div id='page' className='overflow-x-clip bg-offWhite'>
      <GetReviewHeader
        ref={lottieRefHeader}
        handleContinue={handleContinue}
        cartId={cartId}
        cartAp={cartQuery.data?.ap}
        cartLocale={cartQuery.data?.locale}
        cartTotalQty={cartQuery.data?.cartTotalQty || 0}
        pricesAsOf={cartPricesAsOf(cartQuery.data) ?? 0}
        cartTitle={cartQuery.data?.title || undefined}
        cartPrice={cartQuery.data?.formattedCartTotalPrice || '$0.00 USD'}
        cartVendor={cartQuery.data?.vendor!}
        cartVendorDisplayName={cartQuery.data?.vendorDisplayName!}
        cartCreated={cartQuery.data?.ownershipData?.created || false}
        cartStore={cartQuery.data?.store || undefined}
        displaySourceSite={
          cartQuery.data && cartQuery.data.displaySourceSite
            ? (cartQuery.data[
                cartQuery.data.displaySourceSite as keyof CartGetData
              ] as string)
            : undefined
        }
        isLoading={cartQuery.isLoading}
        requestedByEmail={
          cartQuery.data?.approvalDetails?.at(-1)?.requestedBy?.email || ''
        }
        orgApprovalStatus={
          cartQuery.data?.approvalDetails?.at(-1)?.status || undefined
        }
        whitePendingStatus={true}
      />
      {page === 'get' && cartQuery.data && (
        <GetSubscriptionOffers
          store={cartQuery.data.vendor}
          cartValue={Number(cartQuery.data?.cartTotalPrice)}
          hasPrimeDeal={hasPrimeDeal}
        />
      )}
      {page === 'get' &&
        cartQuery.data?.ownershipData &&
        !cartQuery.isLoading && (
          <GetReviewOwnership
            cartTimestamp={cartQuery.data.timestamp || 0}
            cartListed={cartQuery.data.ownershipData?.listed || false}
            cartNotes={cartQuery.data.ownershipData?.note || ''}
            cartTags={cartQuery.data.ownershipData?.tags || []}
            cartTitle={cartQuery.data.title || undefined}
            cartCreated={cartQuery.data.ownershipData?.created || false}
            cartStore={cartQuery.data?.store || undefined}
            cartCCY={cartQuery.data?.cartCCY || 'USD'}
            cartCCYS={cartQuery.data?.cartCCYS || '$'}
            cartItems={cartQuery.data?.items || []}
          />
        )}
      {cartQuery.isLoading ? (
        <LoadingCart customTextColor='text-primary' customBgColor='bg-white' />
      ) : (
        <>
          <GetReviewMissingItems
            missingCount={cartQuery.data?.missingCount || 0}
            cartItemsLength={cartQuery.data?.items.length || 1}
          />
          {chunkVariant === 'modal-and-banner' &&
            cartQuery.data?.vendor === 'amazon' &&
            !isAmazonLocalCart(cartQuery.data?.store) &&
            willAmazonCartChunk(cartQuery.data?.items ?? []) &&
            !searchParams.has('enable_chunk') && (
              <GetReviewChunkBanner onChunkClick={handleChunkBannerClick} />
            )}
          <CartItemsList
            cartItems={cartQuery.data?.items}
            cartCCY={cartQuery.data?.cartCCY}
            cartCCYS={cartQuery.data?.cartCCYS}
            cartVendor={cartQuery.data?.vendor!}
            cartLocale={cartQuery.data?.locale}
            dealForItem={dealForItem}
          />
          <CartBaseNotes notes={cartQuery.data?.note} border />
          {page === 'get' &&
            cartQuery.data &&
            cartQuery.data.items.length >= 10 && (
              <GetSubscriptionOffers
                store={cartQuery.data.vendor}
                cartValue={Number(cartQuery.data?.cartTotalPrice)}
                hasPrimeDeal={hasPrimeDeal}
              />
            )}
          <GetReviewFooter
            ref={lottieRefFooter}
            handleContinue={handleContinue}
            ccy={cartQuery.data?.cartCCY || 'USD'}
            ccyS={cartQuery.data?.cartCCYS || '$'}
            cartPrice={cartQuery.data?.formattedCartTotalPrice || '$0.00 USD'}
            totalQty={cartQuery.data?.cartTotalQty || 0}
            cartTitle={cartQuery.data?.title || undefined}
            cartItems={cartQuery.data?.items || []}
            cartTimestamp={cartQuery.data?.timestamp || 0}
            cartStore={cartQuery.data?.store || undefined}
            isLoading={cartQuery.isLoading}
            page={page}
            requestedByEmail={
              cartQuery.data?.approvalDetails?.at(-1)?.requestedBy?.email || ''
            }
            orgApprovalStatus={
              cartQuery.data?.approvalDetails?.at(-1)?.status || undefined
            }
            cartVendor={cartQuery.data?.vendor}
          />
        </>
      )}

      <RequestApprovalModal
        cartId={cartId}
        show={showRequestApprovalModal}
        setShow={setShowRequestApprovalModal}
        handleContinue={handleContinue}
      />

      <ChunkCartModal cartId={cartId} page={page} />

      <ExtensionInstalledModal
        cartQueryData={cartQuery.data as CartGetData}
        page={page}
      />
      <AmazonManualLoadModal page={page} />
      <ExtensionMissingModal
        numOfItems={cartQuery.data?.cartTotalQty || 0}
        page={page}
      />

      <BlockedRetailerModal cart={cartQuery.data as CartGetData} />
    </div>
  );
};
